import type { TimeFormat } from "../types";

/**
 * Converts a timestamp string to milliseconds
 * Supports SRT format (00:00:24,400) and VTT format (00:00:24.400)
 */
export const toMilliseconds = (timestamp: string): number => {
  if (typeof timestamp !== "string") {
    throw new Error("Timestamp must be a string");
  }

  // Remove any leading/trailing whitespace
  const cleanTimestamp = timestamp.trim();

  // Handle different formats
  if (cleanTimestamp.includes(",")) {
    // SRT format: 00:00:24,400
    return parseSrtTimestamp(cleanTimestamp);
  } else if (cleanTimestamp.includes(".")) {
    // VTT format: 00:00:24.400
    return parseVttTimestamp(cleanTimestamp);
  } else {
    throw new Error(`Unsupported timestamp format: ${timestamp}`);
  }
};

/**
 * Converts milliseconds to SRT timestamp format (00:00:24,400)
 */
export const toSrtTimestamp = (milliseconds: number): string => {
  if (!Number.isFinite(milliseconds) || milliseconds < 0) {
    throw new Error("Milliseconds must be a non-negative number");
  }

  milliseconds = Math.round(milliseconds);
  const hours = Math.floor(milliseconds / 3600000);
  const minutes = Math.floor((milliseconds % 3600000) / 60000);
  const seconds = Math.floor((milliseconds % 60000) / 1000);
  const ms = milliseconds % 1000;

  return `${padZero(hours)}:${padZero(minutes)}:${padZero(seconds)},${padZero(ms, 3)}`;
};

/**
 * Converts milliseconds to VTT timestamp format (00:00:24.400)
 */
export const toVttTimestamp = (milliseconds: number): string => {
  if (!Number.isFinite(milliseconds) || milliseconds < 0) {
    throw new Error("Milliseconds must be a non-negative number");
  }

  milliseconds = Math.round(milliseconds);
  const hours = Math.floor(milliseconds / 3600000);
  const minutes = Math.floor((milliseconds % 3600000) / 60000);
  const seconds = Math.floor((milliseconds % 60000) / 1000);
  const ms = milliseconds % 1000;

  return `${padZero(hours)}:${padZero(minutes)}:${padZero(seconds)}.${padZero(ms, 3)}`;
};

/**
 * Converts a time value to the specified format
 */
export const convertTime = (time: number | string, format: TimeFormat): number | string => {
  if (typeof time === "number") {
    switch (format) {
      case "ms":
        return time;
      case "srt":
        return toSrtTimestamp(time);
      case "vtt":
        return toVttTimestamp(time);
      default:
        throw new Error(`Unsupported time format: ${format}`);
    }
  } else if (typeof time === "string") {
    const ms = toMilliseconds(time);
    return convertTime(ms, format);
  } else {
    throw new Error("Time must be a number or string");
  }
};

/**
 * Validates if a timestamp string is in valid SRT or VTT format
 */
export const isValidTimestamp = (timestamp: string): boolean => {
  try {
    toMilliseconds(timestamp);
    return true;
  } catch {
    return false;
  }
};

// Private helper functions

const parseSrtTimestamp = (timestamp: string): number => {
  const match = timestamp.match(/^(\d{1,2}):(\d{2}):(\d{2}),(\d{3})$/);
  if (!match) {
    throw new Error(`Invalid SRT timestamp format: ${timestamp}`);
  }

  const hours = parseInt(match[1]!, 10);
  const minutes = parseInt(match[2]!, 10);
  const seconds = parseInt(match[3]!, 10);
  const milliseconds = parseInt(match[4]!, 10);

  return hours * 3600000 + minutes * 60000 + seconds * 1000 + milliseconds;
};

const parseVttTimestamp = (timestamp: string): number => {
  const match = timestamp.match(/^(\d{1,2}):(\d{2}):(\d{2})\.(\d{3})$/);
  if (!match) {
    throw new Error(`Invalid VTT timestamp format: ${timestamp}`);
  }

  const hours = parseInt(match[1]!, 10);
  const minutes = parseInt(match[2]!, 10);
  const seconds = parseInt(match[3]!, 10);
  const milliseconds = parseInt(match[4]!, 10);

  return hours * 3600000 + minutes * 60000 + seconds * 1000 + milliseconds;
};

const padZero = (num: number, length = 2): string => {
  return num.toString().padStart(length, "0");
};
